/**
 * Operations items shared by the queues and the user file (OD-71): the server shapes, the headline of
 * each item (amount, state), the queue card and the review sheet with its commands. A command is
 * offered only while the item still waits for a person; decided items open read-only. Amounts are
 * the server's decimal strings; nothing is computed here.
 */
import { BottomSheet, cx, formatJalali, formatRelative } from '@hopex/ui';
import {
  Banknote,
  Bitcoin,
  Check,
  ChevronLeft,
  Coins,
  Copy,
  Landmark,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { ops } from '../lib/api.ts';
import { navigate } from '../lib/platform.ts';
import {
  ActButton,
  ActForm,
  coin,
  Facts,
  Pill,
  toman,
  type Act,
  type Field,
  type Tone,
} from './ops-kit.tsx';
export interface Who {
  userId: string;
  displayName: string | null;
  publicId?: string | null;
}
export interface TomanDeposit extends Who {
  id: string;
  ref: string;
  method: string;
  status: string;
  declaredAmount: string;
  source: { kind: string; masked: string };
  destination: { label: string; holderName: string };
  userReference: string | null;
  /** OD-75: the source card / IBAN (revealed in full on the review sheet, audited). */
  sourceInstrumentId?: string | null;
  createdAt: string;
  version: string;
}
export interface TomanWithdrawal extends Who {
  id: string;
  ref: string;
  status: string;
  amount: string;
  fee: string;
  destination: { kind: string; masked: string; bankCode: string | null };
  holdReason: string | null;
  instrumentId?: string | null;
  createdAt: string;
  version: string;
}
export interface CryptoDeposit extends Who {
  id: string;
  ref: string;
  status: string;
  asset: string;
  networkName: string;
  txid: string | null;
  explorerUrl: string | null;
  evidence: {
    senders: string[];
    amountOnchain: string;
    blockTime: string | null;
    note: string | null;
  } | null;
  screening: { state: string; reason: string };
  createdAt: string;
  version: string;
}
export interface CryptoWithdrawal extends Who {
  id: string;
  ref: string;
  status: string;
  asset: string;
  networkName: string;
  address: string;
  destAmount: string;
  fee: string;
  holdReason: string | null;
  screening: { state: string; reason: string };
  createdAt: string;
  version: string;
}
export interface KycRow {
  id: string;
  userId: string;
  displayName: string | null;
  phoneMasked: string | null;
  level: 'KYC1' | 'KYC2';
  status: string;
  issue: string | null;
  waitingSince: string;
}
export type Item =
  | { k: 'TD'; v: TomanDeposit }
  | { k: 'TW'; v: TomanWithdrawal }
  | { k: 'CD'; v: CryptoDeposit }
  | { k: 'CW'; v: CryptoWithdrawal }
  | { k: 'KYC'; v: KycRow };

export const KIND: Record<Item['k'], { label: string; icon: typeof Banknote }> = {
  TD: { label: 'واریز تومان', icon: Banknote },
  TW: { label: 'برداشت تومان', icon: Landmark },
  CD: { label: 'واریز رمزارز', icon: Coins },
  CW: { label: 'برداشت رمزارز', icon: Bitcoin },
  KYC: { label: 'احراز هویت', icon: ShieldCheck },
};

/** Final states: the item is decided — shown read-only with its outcome. */
const DONE: Record<string, [string, Tone]> = {
  APPROVED_CREDITED: ['واریز شد', 'positive'],
  COMPLETED: ['انجام شد', 'positive'],
  REJECTED: ['ردشده', 'negative'],
  REJECTED_REFUNDED: ['رد و برگشت', 'negative'],
  CANCELLED: ['لغوشده', 'neutral'],
  EXPIRED_NO_TXID: ['منقضی (بدون TXID)', 'neutral'],
  ADDRESS_ISSUED: ['منتظر انتقال کاربر', 'neutral'],
  OTP_PENDING: ['منتظر کد پیامکی کاربر', 'neutral'],
};

/** Is this item still waiting for a person (commands allowed)? */
export function isOpen(it: Item): boolean {
  switch (it.k) {
    case 'TD':
      return it.v.status === 'SUBMITTED' || it.v.status === 'UNDER_REVIEW';
    case 'TW':
      return ['PROCESSING', 'SECURITY_HOLD', 'SETTLEMENT_UNCERTAIN'].includes(it.v.status);
    case 'CD':
      return it.v.status === 'TXID_SUBMITTED' || it.v.status === 'UNDER_REVIEW';
    case 'CW':
      return it.v.status === 'PROCESSING' || it.v.status === 'SECURITY_HOLD';
    case 'KYC':
      return true;
  }
}

export function headline(it: Item): { amount: ReactNode; sub: ReactNode; state: [string, Tone] } {
  const done = it.k === 'KYC' ? undefined : DONE[it.v.status];
  const h = headlineOpen(it);
  return done ? { ...h, state: done } : h;
}

function headlineOpen(it: Item): { amount: ReactNode; sub: ReactNode; state: [string, Tone] } {
  switch (it.k) {
    case 'TD':
      return {
        amount: toman(it.v.declaredAmount),
        sub: <bdi dir="ltr">{it.v.source.masked}</bdi>,
        state: it.v.status === 'UNDER_REVIEW' ? ['در حال بررسی', 'pending'] : ['ثبت‌شده', 'accent'],
      };
    case 'TW':
      return {
        amount: toman(it.v.amount),
        sub: <bdi dir="ltr">{it.v.destination.masked}</bdi>,
        state:
          it.v.status === 'SECURITY_HOLD'
            ? ['نگه‌داشته', 'negative']
            : it.v.status === 'SETTLEMENT_UNCERTAIN'
              ? ['نامشخص', 'pending']
              : ['آماده‌ی پرداخت', 'accent'],
      };
    case 'CD':
      return {
        amount: it.v.evidence ? coin(it.v.evidence.amountOnchain, it.v.asset) : it.v.asset,
        sub: it.v.networkName,
        state: it.v.evidence ? ['تأیید شبکه ✓', 'positive'] : ['در انتظار شبکه', 'pending'],
      };
    case 'CW':
      return {
        amount: coin(it.v.destAmount, it.v.asset),
        sub: it.v.networkName,
        state:
          it.v.status === 'SECURITY_HOLD' ? ['نگه‌داشته', 'negative'] : ['آماده‌ی ارسال', 'accent'],
      };
    case 'KYC':
      return {
        amount: it.v.level === 'KYC1' ? 'سطح ۱' : 'سطح ۲',
        sub: <bdi dir="ltr">{it.v.phoneMasked ?? ''}</bdi>,
        state: ['در انتظار بررسی', 'pending'],
      };
  }
}

export function QueueCard({ it, onOpen }: { it: Item; onOpen: () => void }) {
  const K = KIND[it.k];
  const h = headline(it);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="hx-press hx-card flex w-full cursor-pointer flex-col gap-3 rounded-[22px] border border-line bg-surface-1 p-4 text-start hover:border-line-strong"
      data-ops-item={it.v.id}
    >
      <span className="flex w-full items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-[13px] bg-accent-tint text-accent-text">
          <K.icon aria-hidden className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14.5px] font-extrabold">
            {it.v.displayName ?? 'بدون نام'}
          </span>
          <span className="block truncate text-[11.5px] text-fg-muted">
            {K.label} · {formatRelative(it.k === 'KYC' ? it.v.waitingSince : it.v.createdAt)}
          </span>
        </span>
        <ChevronLeft aria-hidden className="size-4 shrink-0 text-fg-subtle" />
      </span>
      <span className="flex w-full items-end justify-between gap-2 rounded-[16px] bg-surface-2 px-3 py-2.5">
        <span className="min-w-0">
          <span className="block truncate text-[17px] font-black">{h.amount}</span>
          <span className="block truncate text-[11.5px] text-fg-muted">{h.sub}</span>
        </span>
        <Pill tone={h.state[1]}>{h.state[0]}</Pill>
      </span>
    </button>
  );
}

// ───────── full card / IBAN (OD-75) ─────────
const revealed = new Map<string, string>();

/** «1234-5678-…»: the 16-digit card in groups of four; an IBAN in groups of four after «IR». */
const groupFour = (v: string) => v.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');

/**
 * The full card number / IBAN at decision time (owner 2026-10-17): fetched once through the audited
 * reveal (every view is recorded with the agent) and shown with a copy button; the masked value
 * stays while loading or when the agent may not see it.
 */
function FullInstrument({
  userId,
  instrumentId,
  masked,
}: {
  userId: string;
  instrumentId: string | null;
  masked: string;
}) {
  const [value, setValue] = useState<string | null>(
    instrumentId ? (revealed.get(instrumentId) ?? null) : null,
  );
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!instrumentId || value) return;
    let live = true;
    ops
      .run<{ value: string }>(`users/${userId}/reveal`, {
        field: 'BANK_INSTRUMENT',
        instrumentId,
      })
      .then((r) => {
        revealed.set(instrumentId, r.value);
        if (live) setValue(r.value);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [userId, instrumentId, value]);
  if (!value)
    return (
      <bdi dir="ltr" className="font-mono">
        {masked}
      </bdi>
    );
  return (
    <span className="inline-flex items-center gap-1.5" data-full-instrument>
      <bdi dir="ltr" className="font-mono text-[13.5px] font-bold tracking-wide select-all">
        {groupFour(value)}
      </bdi>
      <button
        type="button"
        aria-label="کپی"
        onClick={() =>
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          })
        }
        className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-full bg-surface-2 text-fg-muted"
      >
        {copied ? (
          <Check aria-hidden className="size-4" />
        ) : (
          <Copy aria-hidden className="size-4" />
        )}
      </button>
    </span>
  );
}

// ───────── review sheet ─────────
export function actionsFor(it: Item): { facts: [string, ReactNode][]; acts: Act[] } {
  const reason: Field = { key: 'reason', label: 'دلیل', required: true };
  switch (it.k) {
    case 'TD': {
      const d = it.v;
      return {
        facts: [
          [
            'کد پیگیری',
            <bdi dir="ltr" key="r">
              {d.ref}
            </bdi>,
          ],
          ['مبلغ اعلامی', toman(d.declaredAmount)],
          [
            'از',
            <FullInstrument
              key="s"
              userId={d.userId}
              instrumentId={d.sourceInstrumentId ?? null}
              masked={d.source.masked}
            />,
          ],
          ['به', `${d.destination.label} (${d.destination.holderName})`],
          ['شناسه‌ی کاربر', d.userReference ?? '—'],
          ['ثبت', formatJalali(d.createdAt)],
        ],
        acts: [
          {
            id: 'approve',
            label: 'تأیید و واریز به حساب',
            tone: 'positive',
            fields: [
              {
                key: 'amount',
                label: 'مبلغ نهایی (تومان)',
                value: d.declaredAmount,
                ltr: true,
                required: true,
              },
            ],
            run: (v) =>
              ops.run(`toman-deposits/${d.id}/approve`, {
                amount: v.amount,
                expectedVersion: d.version,
              }),
          },
          {
            id: 'reject',
            label: 'رد',
            tone: 'negative',
            fields: [reason],
            run: (v) =>
              ops.run(`toman-deposits/${d.id}/reject`, {
                reason: v.reason,
                expectedVersion: d.version,
              }),
          },
        ],
      };
    }
    case 'TW': {
      const w = it.v;
      const act =
        (action: string, extra: Record<string, string> = {}) =>
        (v: Record<string, string>) =>
          ops.run(`toman-withdrawals/${w.id}/action`, {
            action,
            reason: v.reason,
            expectedVersion: w.version,
            ...extra,
            ...(v.trackingRef ? { trackingRef: v.trackingRef, rail: 'OTHER' } : {}),
          });
      return {
        facts: [
          [
            'کد پیگیری',
            <bdi dir="ltr" key="r">
              {w.ref}
            </bdi>,
          ],
          ['مبلغ', toman(w.amount)],
          ['کارمزد', toman(w.fee)],
          [
            'مقصد',
            <FullInstrument
              key="d"
              userId={w.userId}
              instrumentId={w.instrumentId ?? null}
              masked={w.destination.masked}
            />,
          ],
          ['وضعیت', w.holdReason ? `نگه‌داشته: ${w.holdReason}` : 'در حال انجام'],
        ],
        acts: [
          {
            id: 'complete',
            label: 'پرداخت شد',
            tone: 'positive',
            fields: [
              { key: 'trackingRef', label: 'شماره پیگیری بانک', ltr: true, required: true },
              reason,
            ],
            run: act('COMPLETE'),
          },
          {
            id: 'hold',
            label: 'نگه‌داشتن',
            tone: 'neutral',
            fields: [reason],
            run: act(w.status === 'SECURITY_HOLD' ? 'RESUME' : 'HOLD'),
          },
          {
            id: 'reject',
            label: 'رد و بازگشت پول',
            tone: 'negative',
            fields: [reason],
            run: act('REJECT'),
          },
        ],
      };
    }
    case 'CD': {
      const d = it.v;
      const needAccept = d.screening.state === 'NEEDS_ACCEPTANCE';
      return {
        facts: [
          [
            'کد پیگیری',
            <bdi dir="ltr" key="r">
              {d.ref}
            </bdi>,
          ],
          ['ارز و شبکه', `${d.asset} · ${d.networkName}`],
          [
            'مبلغ روی شبکه',
            d.evidence ? coin(d.evidence.amountOnchain, d.asset) : 'هنوز تأیید نشده',
          ],
          [
            'تراکنش',
            d.explorerUrl ? (
              <a
                key="x"
                href={d.explorerUrl}
                target="_blank"
                rel="noopener"
                className="text-accent-text underline"
              >
                مشاهده در شبکه
              </a>
            ) : (
              '—'
            ),
          ],
          ['توضیح', d.evidence?.note ?? '—'],
        ],
        acts: [
          ...(d.evidence
            ? [
                {
                  id: 'approve',
                  label: 'تأیید و واریز به حساب',
                  tone: 'positive' as const,
                  fields: [reason],
                  ...(needAccept
                    ? {
                        checkbox: {
                          key: 'accept',
                          label: 'بدون سرویس غربالگری، این واریز را می‌پذیرم',
                        },
                      }
                    : {}),
                  run: (v: Record<string, string>, checked: boolean) =>
                    ops.run(`crypto-deposits/${d.id}/approve`, {
                      finalAmount: d.evidence?.amountOnchain,
                      expectedVersion: d.version,
                      reason: v.reason,
                      acceptScreeningReview: checked,
                    }),
                },
              ]
            : []),
          {
            id: 'reject',
            label: 'رد',
            tone: 'negative',
            fields: [reason],
            run: (v) =>
              ops.run(`crypto-deposits/${d.id}/reject`, {
                reason: v.reason,
                expectedVersion: d.version,
              }),
          },
        ],
      };
    }
    case 'CW': {
      const w = it.v;
      const act = (action: string) => (v: Record<string, string>, checked: boolean) =>
        ops.run(`crypto-withdrawals/${w.id}/action`, {
          action,
          reason: v.reason,
          expectedVersion: w.version,
          ...(v.payoutTxid ? { payoutTxid: v.payoutTxid } : {}),
          ...(checked ? { acceptScreeningReview: true } : {}),
        });
      return {
        facts: [
          [
            'کد پیگیری',
            <bdi dir="ltr" key="r">
              {w.ref}
            </bdi>,
          ],
          ['مبلغ', coin(w.destAmount, w.asset)],
          ['کارمزد', coin(w.fee, w.asset)],
          ['شبکه', w.networkName],
          [
            'آدرس مقصد',
            <bdi dir="ltr" key="a" className="break-all font-mono text-[12px]">
              {w.address}
            </bdi>,
          ],
        ],
        acts: [
          {
            id: 'complete',
            label: 'ارسال شد',
            tone: 'positive',
            fields: [{ key: 'payoutTxid', label: 'TXID ارسال', ltr: true, required: true }, reason],
            ...(w.screening.state === 'NEEDS_ACCEPTANCE'
              ? {
                  checkbox: { key: 'accept', label: 'بدون سرویس غربالگری، این برداشت را می‌پذیرم' },
                }
              : {}),
            run: act('COMPLETE'),
          },
          {
            id: 'hold',
            label: w.status === 'SECURITY_HOLD' ? 'ادامه' : 'نگه‌داشتن',
            tone: 'neutral',
            fields: [reason],
            run: act(w.status === 'SECURITY_HOLD' ? 'RESUME' : 'HOLD'),
          },
          {
            id: 'reject',
            label: 'رد و بازگشت',
            tone: 'negative',
            fields: [reason],
            run: act('REJECT'),
          },
        ],
      };
    }
    case 'KYC':
      return { facts: [], acts: [] };
  }
}

export function ReviewSheet({
  item,
  onClose,
  onDone,
  showUserLink = true,
}: {
  item: Item;
  onClose: () => void;
  onDone: () => void;
  /** Hidden when the sheet is already opened from that user's file. */
  showUserLink?: boolean;
}) {
  const all = actionsFor(item);
  const facts: [string, ReactNode][] = all.facts.some(([k]) => k === 'ثبت')
    ? all.facts
    : [
        ...all.facts,
        ['ثبت', formatJalali(item.k === 'KYC' ? item.v.waitingSince : item.v.createdAt)],
      ];
  // Decided items are read-only: no command is offered once the outcome is final.
  const acts = isOpen(item) ? all.acts : [];
  const [act, setAct] = useState<Act | null>(null);
  const h = headline(item);
  const K = KIND[item.k];
  return (
    <BottomSheet
      isOpen
      onOpenChange={(o) => !o && onClose()}
      title={item.v.displayName ?? 'بدون نام'}
    >
      <div className="flex flex-col gap-3 pb-2" data-ops-review>
        <div
          className="relative overflow-hidden rounded-[20px] border border-line-strong bg-canvas-deep p-4 text-fg"
          data-theme-scope="dark"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_140%_at_100%_0%,color-mix(in_srgb,var(--hx-accent)_24%,transparent),transparent_65%)]"
          />
          <p className="relative m-0 flex items-center gap-1.5 text-[12px] text-fg-muted">
            <K.icon aria-hidden className="size-4" /> {K.label}
          </p>
          <p className="relative m-0 mt-1.5 text-[24px] font-black">{h.amount}</p>
          <div className="relative mt-2">
            <Pill tone={h.state[1]}>{h.state[0]}</Pill>
          </div>
        </div>
        <div className="rounded-[18px] border border-line bg-surface-1 px-3.5 py-1">
          <Facts rows={facts} />
        </div>
        {showUserLink && (
          <button
            type="button"
            onClick={() => navigate(`/ops/u/${item.v.userId}`)}
            className="hx-press inline-flex min-h-10 w-fit cursor-pointer items-center gap-1.5 rounded-full bg-surface-2 px-3.5 text-[12.5px] font-bold text-accent-text"
          >
            <UserRound aria-hidden className="size-4" /> پرونده‌ی کاربر
          </button>
        )}
        {!isOpen(item) && (
          <p className="m-0 rounded-[14px] bg-surface-2 px-3 py-2.5 text-center text-[12.5px] text-fg-muted">
            این مورد تعیین تکلیف شده است؛ فقط مشاهده.
          </p>
        )}
        {act ? (
          <ActForm act={act} onCancel={() => setAct(null)} onDone={onDone} />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {acts.map((a, i) => (
              <div key={a.id} className={cx(i === 0 && a.tone === 'positive' && 'col-span-2')}>
                <ActButton a={a} onPick={setAct} />
              </div>
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
